import {Directive, Injector} from '@angular/core';
import {New} from '@js-dot/core';
import {getKrPp, getPlural} from '@js-dot/text';
import {DotTranslateService} from '@ng-dot/text';
import {ListAdjust} from '@ng-dot/ui/dualist';
import {TextReplaceCache} from '../typings/text-cache';
import {TextInterface} from '../typings/text-interface';
import {TextService} from '../services/text.service';
import { semiMarkdown } from '@js-dot/frame';

const $_variables   = /\{\{([^}]+)}\}/g;
const $_commands    = /([^\s]+)\([^)]+\)/g;
const $_termTrigger = /^([A-Z][A-Z\d]+)(?:_[A-Z\d]+)*(\.\w+)?$/;

/**
 * 다중 언어를 지원을 위한 문자열 기반
 * @deprecated `SelfLazyTextService` 사용
 */
@Directive()
// tslint:disable-next-line:directive-class-suffix
export abstract class TextBase implements TextInterface {
	protected _tokens: Record<string, TextReplaceCache> = {};
	protected _injector: Injector;
	protected _text: TextService;
	protected _translate: DotTranslateService;
	abstract readonly en: Record<string, string>;
	abstract readonly kr: Record<string, string>;

	readonly extendTexts!: Array<New<TextBase>>;

	get suffix() {
		return this._text.suffix;
	}

	constructor(injector: Injector) {
		this._injector  = injector;
		this._text      = injector.get(TextService);
		this._translate = injector.get(DotTranslateService);
	}

	protected _getToken(text: string) {
		const {_translate} = this;
		const variables    = text.match($_variables);

		let replaces;

		if (variables) {
			replaces    = [];
			let current = variables.length;
			let variable;
			let name;
			let termTrigger;
			let term;
			while (current-- > 0) {
				variable    = variables[current];
				name        = variable.substring(2, variable.length - 2).trim();
				termTrigger = name.match($_termTrigger);

				if (!termTrigger) {
					replaces.push([variable, name]);
					continue;
				}

				term = termTrigger[1];
				text = text.replace(variable, _translate.define(name));
			}
		}

		text = semiMarkdown(text);

		return {
			text,
			replaces
		};
	}

	protected _getLabelToken(label: string): TextReplaceCache {
		const {_tokens} = this;

		if (_tokens[label]) {
			return _tokens[label];
		}

		const {language, baseLanguage} = this._text;
		const {_translate}             = this;

		const mainTexts = this[language];
		const baseTexts = this[baseLanguage];
		let text        = mainTexts && mainTexts.hasOwnProperty(label) ?
			mainTexts[label] :
			baseTexts && baseTexts[label];

		let cache;

		if (text || text === '') {
			cache = this._getToken(text);
		} else {
			const {extendTexts} = this;
			if (extendTexts) {
				const {_injector} = this;
				const end         = extendTexts.length;
				let current       = -1;
				let parentText;
				while (++current < end) {
					parentText = _injector.get(extendTexts[current]);
					if (parentText.has(label)) {
						cache = parentText._getLabelToken(label);
						break;
					}
				}
			}
		}

		if (!cache) {
			cache = {
				text: 'Text::' + label,
				replaces: null
			};
		}

		_tokens[label] = cache;

		return cache;
	}

	protected _getTemplatedString(token, values: boolean | number | string | Record<string, any>) {
		const {replaces} = token;
		let {text}       = token;

		if (values || values === 0) {
			if (replaces) {
				text = replaces.reduce((result, replace) => {
					const key  = replace[1];
					let value_ = key === 'this' ?
						values :
						values[key];

					if (typeof value_ === 'number') {
						value_ = value_.toLocaleString();
					}
					return result.replace(replace[0], value_);
				}, text);
			}
		}

		const commands = text.match($_commands);
		if (commands) {
			const _commands = commands.reduce(commandParser, []);
			text            = _commands.reduce((result, command) => {
				return result.replace(command.command, command.method(command.word, command.args, values));
			}, text);
		}

		return text;
	}

	has(label: keyof this['en'] | string) {
		const {language, baseLanguage} = this._text;

		const result = !!(
			this[language][label]
			|| this[baseLanguage][label]
			|| this._tokens[label as any]
		);

		if (result) {
			return true;
		}

		const {extendTexts} = this;
		if (!extendTexts || !extendTexts.length) {
			return false;
		}

		const {_injector} = this;
		const end         = extendTexts.length;
		let current       = -1;
		let extendText;
		while (++current < end) {
			extendText = _injector.get(extendTexts[current]);
			if (extendText.has(label)) {
				return true;
			}
		}

		return false;
	}

	/**
	 * get text
	 * @param label
	 * @param values
	 */
	get(label: keyof this['en'] | string, values?: boolean | number | string | Record<string, any>): string;
	get(label, value?) {
		return this._getTemplatedString(
			this._getLabelToken(label),
			value
		);
	}

	/**
	 * Get Multiple Texts by Array
	 * @param labels
	 */
	gets<T extends keyof this['en'] | string>(labels: T[]): Record<T, string>;

	/**
	 * Get Multiple Texts by Array as Arguments
	 * @param labels
	 */
	gets<T extends keyof this['en'] | string>(...labels: T[]): Record<T, string>;

	/**
	 * Get Multiple Text by Re-name Map
	 * @description
	 *   gets({foo: 'baz'}) => {foo: this.get('baz')};
	 * @param labelMap
	 */
	gets<T extends keyof this['en'] | string, U extends string>(labelMap: Record<U, T>): Record<U, string>;

	gets(...labels) {
		const first = labels[0];
		if (Array.isArray(first)) {
			labels = first;
		} else if (typeof first === 'object') {
			return Object.keys(first).reduce((stringHas, key) => {
				const label    = first[key] || key;
				stringHas[key] = this.get(label);
				return stringHas;
			}, {});
		}

		return labels.reduce((result, label) => {
			result[label] = this.get(label);
			return result;
		}, {});
	}

	_getKeys(): Array<keyof this['en'] | keyof this['kr']> {
		const filteredKeys = [];
		const values       = this[this._text.baseLanguage];
		for (let key in values) {
			if (!values.hasOwnProperty(key)) {
				continue;
			}
			if (key.charAt(0) !== '$') {
				filteredKeys[filteredKeys.length] = key;
			}
		}
		return filteredKeys;
	}

	/**
	 * 모든 정적 문자열 얻기
	 */
	getAll(): Record<keyof this['en'] | keyof this['kr'], string> {
		const keys = this._getKeys();

		const {extendTexts} = this;
		if (extendTexts) {
			const {_injector} = this;
			const end         = extendTexts.length;
			let current       = -1;
			while (++current < end) {
				const extendText = _injector.get(extendTexts[current]);
				keys.push(...extendText._getKeys());
			}
		}

		return this.gets.apply(this, keys);
	}

	/**
	 * @alias TextService.selectName
	 * @param target
	 */
	selectName(target: string) {
		return this._text.selectName(target);
	}

	/**
	 * @alias TextService.service
	 * @param target
	 * @param property
	 */
	select<T extends {}>(target: T, property?: keyof T | string) {
		return this._text.select(target, property);
	}

	addSuffix(target: string): string {
		return this._text.addSuffix(target);
	}

	removeSuffix(target: string): string {
		return this._text.removeSuffix(target);
	}

	make(text: string, values?: Record<string, any>) {
		return this._getTemplatedString(
			this._getToken(text),
			values
		);
	}
}

const SYNTAXES = [
	{
		args: /^s$/,
		method(word, args, value) {
			const amount = args[1];
			if (amount) {
				value = value[amount];
			}

			return getPlural(word, value);
		}
	},
	{
		args: /^[을를이가은는로]$/,
		method(word, args) {
			const ppWord = word
				.replace(/[oe]r$/, '어');

			return word + getKrPp(ppWord, args[0]);
		}
	}
];

function commandParser(commands, command) {
	const splitCommand = command.match(/([^(\s]+)\(([^)]+)\)/);

	const word      = splitCommand[1];
	const argString = splitCommand[2].trim();
	const args      = argString.split(/\s*,\s*/);
	const arg       = args[0];

	SYNTAXES.some(syntax => {
		if (syntax.args.test(arg)) {
			commands.push({
				command,
				word,
				method: syntax.method,
				args
			});
			return true;
		}
		return false;
	});

	return commands;
}
